import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  InputBase,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "10px 16px",
    backgroundColor: theme.palette.tabHeaderBackground,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: "56px",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
    color: theme.palette.textPrimary,
  },
  searchContainer: {
    padding: "8px 12px",
    backgroundColor: theme.palette.tabHeaderBackground,
  },
  searchInput: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.mode === "light" ? "#f0f2f5" : "#2a3942",
    borderRadius: "8px",
    padding: "2px 12px",
    height: "35px",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    marginRight: "8px",
    fontSize: "20px",
  },
  inputBase: {
    flex: 1,
    fontSize: "14px",
  },
  contactList: {
    flex: 1,
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)",
      borderRadius: "3px",
    },
  },
  contactItem: {
    cursor: "pointer",
    padding: "10px 16px",
    "&:hover": {
      backgroundColor: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.04)"
        : "rgba(255, 255, 255, 0.04)",
    },
    "&.selected": {
      backgroundColor: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.08)",
    },
  },
  contactNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    overflow: "hidden",
  },
  contactName: {
    fontWeight: 500,
    fontSize: "15px",
    color: theme.palette.textPrimary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  badgeTag: {
    fontSize: "10px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "4px",
    whiteSpace: "nowrap",
    lineHeight: "16px",
  },
  queueBadge: {
    color: "#fff",
  },
  whatsappBadge: {
    backgroundColor: theme.mode === "light" ? "#e8f5e9" : "#1b3a1f",
    color: theme.mode === "light" ? "#2e7d32" : "#81c784",
  },
  lastMessage: {
    fontSize: "13px",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "220px",
  },
  avatar: {
    width: 49,
    height: 49,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "20px",
  },
  noResults: {
    textAlign: "center",
    padding: "20px",
    color: theme.palette.text.secondary,
    fontSize: "14px",
  },
  timestamp: {
    fontSize: "11px",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  itemRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    minWidth: "55px",
    gap: "4px",
  },
  unreadBadge: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderRadius: "50%",
    minWidth: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    fontWeight: 600,
  },
}));

const ContactListDirect = ({ onContactSelect, selectedContactId }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const listRef = useRef(null);
  const searchTimeout = useRef(null);

  const fetchContacts = useCallback(async (page, search, append = false) => {
    setLoading(true);
    try {
      const { data: directData } = await api.get("/contacts/direct-chat", {
        params: {
          searchParam: search,
          pageNumber: page,
        },
      });

      // Build list directly from tickets (backend already includes contact data)
      const enriched = (directData?.tickets || [])
        .filter((t) => t.contact)
        .map((t) => ({
          ...t.contact,
          ticket: t,
          lastMessage: t.lastMessage || "",
          lastMessageAt: t.updatedAt || t.contact.updatedAt,
        }));

      if (append) {
        setContacts((prev) => {
          const ids = new Set(prev.map((c) => c.id));
          const newContacts = enriched.filter((c) => !ids.has(c.id));
          return [...prev, ...newContacts];
        });
      } else {
        setContacts(enriched);
      }
      setHasMore(directData.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageNumber(1);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      fetchContacts(1, searchParam, false);
    }, 400);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchParam, fetchContacts]);

  // Real-time: update lastMessage when new messages arrive
  useEffect(() => {
    if (!socket) return;

    const onAppMessage = (data) => {
      if (data.action === "create" && data.message) {
        const contactId = Number(data.ticket?.contactId || data.contact?.id);
        setContacts((prev) => {
          // Match by contactId (covers all tickets for this contact)
          let idx = prev.findIndex((c) => Number(c.id) === contactId);
          // Fallback: match by ticket id
          if (idx === -1) {
            idx = prev.findIndex((c) => c.ticket?.id === data.message.ticketId);
          }
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              lastMessage: data.message.body,
              lastMessageAt: data.message.createdAt,
            };
            // Move to top
            const [item] = updated.splice(idx, 1);
            updated.unshift(item);
            return updated;
          }
          return prev;
        });
      }
    };

    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    return () => {
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, companyId]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loading) {
      const nextPage = pageNumber + 1;
      setPageNumber(nextPage);
      fetchContacts(nextPage, searchParam, true);
    }
  };

  const handleClearSearch = () => {
    setSearchParam("");
  };

  const formatTimestamp = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    } else if (days === 1) {
      return "Ontem";
    } else if (days < 7) {
      return d.toLocaleDateString("pt-BR", { weekday: "short" });
    }
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  const truncateMessage = (msg) => {
    if (!msg) return "";
    if (msg.length > 40) return msg.substring(0, 40) + "...";
    return msg;
  };

  const handleClick = (contact) => {
    onContactSelect(contact, contact.ticket);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>Conversas</Typography>
      </div>

      <div className={classes.searchContainer}>
        <div className={classes.searchInput}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.inputBase}
            placeholder="Pesquisar conversa..."
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
          />
          {searchParam && (
            <IconButton size="small" onClick={handleClearSearch}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>

      <List
        className={classes.contactList}
        ref={listRef}
        onScroll={handleScroll}
      >
        {contacts.map((contact) => (
          <React.Fragment key={contact.id}>
            <ListItem
              className={`${classes.contactItem} ${
                selectedContactId === contact.id ? "selected" : ""
              }`}
              onClick={() => handleClick(contact)}
              alignItems="flex-start"
            >
              <ListItemAvatar>
                <Avatar
                  className={classes.avatar}
                  src={contact.profilePicUrl || contact.urlPicture}
                  alt={contact.name}
                >
                  {contact.name?.charAt(0).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <div className={classes.contactNameRow}>
                    <Typography className={classes.contactName}>
                      {contact.name || contact.number}
                    </Typography>
                    {contact.ticket?.queue && (
                      <span
                        className={`${classes.badgeTag} ${classes.queueBadge}`}
                        style={{ backgroundColor: contact.ticket.queue.color || "#7c7c7c" }}
                      >
                        {contact.ticket.queue.name}
                      </span>
                    )}
                    {contact.ticket?.whatsapp && (
                      <span className={`${classes.badgeTag} ${classes.whatsappBadge}`}>
                        {contact.ticket.whatsapp.name}
                      </span>
                    )}
                  </div>
                }
                secondary={
                  <Typography component="span" className={classes.lastMessage}>
                    {truncateMessage(contact.lastMessage) || contact.number}
                  </Typography>
                }
              />
              <div className={classes.itemRight}>
                {contact.lastMessage && (
                  <Typography className={classes.timestamp}>
                    {formatTimestamp(contact.lastMessageAt)}
                  </Typography>
                )}
                {contact.ticket?.unreadMessages > 0 && (
                  <div className={classes.unreadBadge}>
                    {contact.ticket.unreadMessages}
                  </div>
                )}
              </div>
            </ListItem>
            <Divider variant="inset" component="li" />
          </React.Fragment>
        ))}

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        )}

        {!loading && contacts.length === 0 && (
          <div className={classes.noResults}>
            {searchParam
              ? "Nenhum contato encontrado"
              : "Nenhum contato disponivel"}
          </div>
        )}
      </List>
    </div>
  );
};

export default ContactListDirect;
